import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getConciliations,
  getConciliationDetailByKey,
} from "modules/finops/conciliaciones/services/financieraService";

const PAGE_SIZE = 10000;

async function loadGlobalRecords(params, signal) {
  const records = new Map();
  for (let page = 1; !signal.aborted; page += 1) {
    const result = await getConciliations({ page, limit: PAGE_SIZE, ...params }, signal);
    if (signal.aborted || !result.success) return result;
    const data = result.data || [];
    const receivedCount = result.receivedCount ?? data.length;
    const previousSize = records.size;
    data.forEach((record) => {
      const identity =
        record.id != null
          ? `id:${record.id}`
          : record.uuid
          ? `uuid:${record.uuid}`
          : JSON.stringify(record);
      records.set(identity, record);
    });
    if (receivedCount < PAGE_SIZE) {
      const all = [...records.values()];
      all.sort((a, b) => {
        const aCompleted = String(a.status) === "2";
        const bCompleted = String(b.status) === "2";
        return aCompleted !== bCompleted ? (aCompleted ? -1 : 1) : Number(b.id) - Number(a.id);
      });
      return { success: true, data: all };
    }
    if (records.size === previousSize) {
      throw new Error(
        "El servidor no permite avanzar en el listado. No se pudo cargar la lista completa."
      );
    }
  }
  return null;
}

function creationDay(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
}

export default function useLoteRecords({
  llaveSeleccionada,
  fechaInicio,
  fechaFin,
  filtroVertical,
  filtroStatus,
  refreshFlag,
  onError,
}) {
  const [records, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [keySummary, setKeySummary] = useState(null);
  const requestRef = useRef(null);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const cancelRequest = useCallback(() => {
    requestRef.current?.abort();
    requestRef.current = null;
  }, []);

  const fetchData = useCallback(
    async (key = "") => {
      cancelRequest();
      const controller = new AbortController();
      requestRef.current = controller;
      const isCurrent = () => requestRef.current === controller && !controller.signal.aborted;
      setLoading(true);
      setError(null);
      setAllRecords([]);
      setKeySummary(null);

      const params = {};
      if (filtroVertical) params.vertical = filtroVertical;
      if (filtroStatus && filtroStatus !== "todos") params.status = filtroStatus;

      try {
        const result = key
          ? await getConciliationDetailByKey(key, params, controller.signal)
          : await loadGlobalRecords(params, controller.signal);
        if (!isCurrent()) return;
        if (!result.success) throw new Error(result.message || "Error al cargar conciliaciones");
        setAllRecords(result.data || []);
        if (key) {
          setKeySummary({
            key,
            totalRegistros: result.totalRegistros || 0,
            valorTotal: result.valorTotal || 0,
            totalAbonoNov: result.totalAbonoNov || 0,
            totalAjusteNov: result.totalAjusteNov || 0,
            conciliationId: result.conciliationId || null,
          });
        }
        onErrorRef.current?.(null);
      } catch (err) {
        if (!isCurrent()) return;
        const message = err.message || "Error al cargar conciliaciones";
        setError(message);
        onErrorRef.current?.(message);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    },
    [cancelRequest, filtroVertical, filtroStatus]
  );

  useEffect(() => {
    fetchData(String(llaveSeleccionada || "").trim());
    return cancelRequest;
  }, [fetchData, llaveSeleccionada, refreshFlag, cancelRequest]);

  const fetchRecords = useCallback(() => fetchData(), [fetchData]);
  const fetchByKey = useCallback((key) => fetchData(String(key).trim()), [fetchData]);

  const invalidRange = !llaveSeleccionada && fechaInicio && fechaFin && fechaInicio > fechaFin;
  const allRecords = useMemo(() => {
    if (llaveSeleccionada || (!fechaInicio && !fechaFin)) return records;
    if (invalidRange) return [];
    return records.filter((record) => {
      const day = creationDay(record.created_at);
      return day && (!fechaInicio || day >= fechaInicio) && (!fechaFin || day <= fechaFin);
    });
  }, [records, llaveSeleccionada, fechaInicio, fechaFin, invalidRange]);

  return {
    allRecords,
    loading,
    error: invalidRange ? "La fecha inicial no puede ser posterior a la fecha final." : error,
    keySummary,
    fetchRecords,
    fetchByKey,
  };
}
